import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { barterApi, reviewApi } from '@/services/api'
import Card from '@/components/ui/Card'
import Loading from '@/components/ui/Loading'
import Badge from '@/components/ui/Badge'
import {
  TrendingUp,
  Package,
  CheckCircle,
  Star,
  Coins,
  DollarSign,
  BarChart3,
  Lock
} from 'lucide-react'
import { formatNumber } from '@/utils/format'

export default function Analytics() {
  const { user } = useAuth()
  const navigate = useNavigate()

  // Check if user has access to analytics
  const hasAccess = user?.subscription_tier === 'paid' && user?.subscription_status === 'active'

  const { data: bartersData, isLoading: bartersLoading } = useQuery({
    queryKey: ['user-barters-all'],
    queryFn: () => barterApi.list({ my_barters: 'true', limit: 1000 }),
    enabled: hasAccess,
  })

  const { data: reviewsData, isLoading: reviewsLoading } = useQuery({
    queryKey: ['user-reviews-all'],
    queryFn: () => reviewApi.list({ reviewee_id: user?.id }),
    enabled: hasAccess && !!user?.id,
  })

  // Redirect non-paid users
  if (!hasAccess) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card>
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-yellow-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Advanced Analytics</h2>
            <p className="text-gray-600 mb-6">
              Upgrade to Pro to unlock detailed analytics and insights about your bartering activity
            </p>
            <button
              onClick={() => navigate('/dashboard/subscription')}
              className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
            >
              Upgrade to Pro
            </button>
          </div>
        </Card>
      </div>
    )
  }

  if (bartersLoading || reviewsLoading) return <Loading />

  const userBarters = bartersData?.data?.barters || []
  const reviews = reviewsData?.data?.reviews || []

  // Calculate statistics
  const totalBarters = userBarters.length
  const activeBarters = userBarters.filter((b: any) => b.visibility === 'active').length
  const completedBarters = userBarters.filter((b: any) => b.status === 'completed').length
  const inProgressBarters = userBarters.filter((b: any) => b.status === 'in_progress').length

  const totalValue = userBarters.reduce((sum: number, b: any) => sum + (Number(b.dollar_value) || 0), 0)
  const averageValue = totalBarters > 0 ? totalValue / totalBarters : 0

  const averageRating = reviews.length > 0
    ? reviews.reduce((sum: number, r: any) => sum + r.rating, 0) / reviews.length
    : 0

  const completionRate = totalBarters > 0
    ? ((completedBarters / totalBarters) * 100)
    : 0

  // Group barters by type
  const bartersByType = userBarters.reduce((acc: any, b: any) => {
    acc[b.type] = (acc[b.type] || 0) + 1
    return acc
  }, {})

  // Group barters by status
  const bartersByStatus = userBarters.reduce((acc: any, b: any) => {
    acc[b.status] = (acc[b.status] || 0) + 1
    return acc
  }, {})

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center space-x-3">
            <BarChart3 className="w-8 h-8 text-primary-600" />
            <span>Advanced Analytics</span>
          </h1>
          <p className="text-gray-600 mt-2">
            Detailed insights into your bartering activity
          </p>
        </div>
        <Badge variant="warning">Pro Member</Badge>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Barters</p>
              <p className="text-2xl font-bold text-gray-900">{totalBarters}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">{completedBarters}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Value</p>
              <p className="text-2xl font-bold text-gray-900">${formatNumber(totalValue)}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Star className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Avg Rating</p>
              <p className="text-2xl font-bold text-gray-900">
                {averageRating > 0 ? averageRating.toFixed(1) : 'N/A'}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Performance Overview">
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">Completion Rate</span>
                <span className="text-sm font-semibold text-gray-900">{completionRate.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${completionRate}%` }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <p className="text-xs text-gray-500 mb-1">Average Value</p>
                <p className="text-lg font-bold text-gray-900">${formatNumber(averageValue)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Active Barters</p>
                <p className="text-lg font-bold text-gray-900">{activeBarters}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <p className="text-xs text-gray-500 mb-1">In Progress</p>
                <p className="text-lg font-bold text-blue-600">{inProgressBarters}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Reviews</p>
                <p className="text-lg font-bold text-purple-600">{reviews.length}</p>
              </div>
            </div>
          </div>
        </Card>

        <Card title="Barter Distribution">
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">By Status</h4>
              <div className="space-y-2">
                {Object.entries(bartersByStatus).map(([status, count]: [string, any]) => (
                  <div key={status} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Badge status={status}>{status}</Badge>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">By Type</h4>
              <div className="space-y-2">
                {Object.entries(bartersByType).map(([type, count]: [string, any]) => (
                  <div key={type} className="flex items-center justify-between">
                    <Badge variant={type === 'goods' ? 'primary' : 'success'}>
                      {type}
                    </Badge>
                    <span className="text-sm font-semibold text-gray-900">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Additional Insights */}
      <Card title="Insights & Recommendations">
        <div className="space-y-4">
          {completionRate < 50 && totalBarters > 0 && (
            <div className="flex items-start space-x-3 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <TrendingUp className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-yellow-900">Improve Completion Rate</p>
                <p className="text-sm text-yellow-700 mt-1">
                  Your completion rate is below 50%. Focus on completing your active barters to build trust and improve your reputation.
                </p>
              </div>
            </div>
          )}

          {reviews.length === 0 && completedBarters > 0 && (
            <div className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <Star className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-blue-900">Request Reviews</p>
                <p className="text-sm text-blue-700 mt-1">
                  You have completed barters but no reviews yet. Encourage your barter partners to leave reviews to build credibility.
                </p>
              </div>
            </div>
          )}

          {averageRating >= 4.5 && reviews.length >= 5 && (
            <div className="flex items-start space-x-3 p-4 bg-green-50 rounded-lg border border-green-200">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-green-900">Excellent Reputation!</p>
                <p className="text-sm text-green-700 mt-1">
                  Your average rating of {averageRating.toFixed(1)} stars shows you're a trusted member of the community. Keep up the great work!
                </p>
              </div>
            </div>
          )}

          {totalBarters === 0 && (
            <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <Package className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-gray-900">Get Started</p>
                <p className="text-sm text-gray-700 mt-1">
                  You haven't created any barters yet. Start by creating your first barter listing to begin trading with the community.
                </p>
                <button
                  onClick={() => navigate('/dashboard/barters/create')}
                  className="mt-3 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Create Your First Barter
                </button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Barter Coin Summary */}
      <Card title="Barter Coin Summary">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center">
              <Coins className="w-8 h-8 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Current Balance</p>
              <p className="text-3xl font-bold text-primary-700">{formatNumber(user?.barter_coin_balance || 0)}</p>
              <p className="text-xs text-gray-500 mt-1">Barter Coins</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/dashboard/coins')}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
          >
            Manage Coins
          </button>
        </div>
      </Card>
    </div>
  )
}
